import { RefObject, useCallback, useLayoutEffect, useState } from "react";

/**
 * ONE ResizeObserver for every truncating element in the app, not one per
 * element.
 *
 * Table cells wrap every value in this, so a 50-row page created ~550
 * observers — measured on the guestlist at the default page size. Each is a
 * separate registration the engine has to service after layout, and iOS Safari
 * pays noticeably more for that than Chrome does, which is the shape of the
 * scroll stutter reported on iPhone (smooth in desktop Chrome's mobile
 * emulation, stuttery on the device).
 *
 * A single observer with many observed targets has the same semantics and a
 * fraction of the bookkeeping.
 */
const overflowCallbacks = new WeakMap<Element, () => void>();
let sharedObserver: ResizeObserver | null = null;

export const observeOverflow = (el: Element, cb: () => void): (() => void) => {
  if (typeof ResizeObserver === "undefined") return () => {};
  if (!sharedObserver) {
    sharedObserver = new ResizeObserver((entries) => {
      for (const entry of entries) overflowCallbacks.get(entry.target)?.();
    });
  }
  overflowCallbacks.set(el, cb);
  sharedObserver.observe(el);
  return () => {
    overflowCallbacks.delete(el);
    sharedObserver?.unobserve(el);
  };
};

/**
 * Is this element's content wider than the box it is drawn in?
 *
 * Continuously true-to-life, not sampled on hover: the answer also gates a
 * native `title`, which has to be correct for a pointer that never hovers at
 * all (a trackpad on iPadOS, where the OS reports `hover: none`).
 *
 * Measurement is split from observation on purpose. The observer is built ONCE
 * per element — it used to be rebuilt on every render, because the effect was
 * keyed on `children`, which is a freshly-created element each pass; a 50-row
 * page is hundreds of cells, and that was the most expensive thing in a table
 * render. `deps` re-runs only the (cheap, layout-batched) read, which is needed
 * because the rendered text can change under an unchanged box: an option-source
 * select renders "" until its labels resolve and the human name afterwards,
 * inside a fixed-width cell that never fires the observer.
 */
export const useOverflowing = (
  ref: RefObject<HTMLElement | null>,
  deps: unknown[] = [],
  /**
   * Also "overflowing" when a descendant matching this selector is cut — for
   * content that truncates INSIDE a box that never overflows itself (the guest
   * list's seat chip gives up its parts one by one: seats.io Teil 2, Zug 3,
   * audit r2 FY-5). The observed element is still the only one watched: a cut
   * inside follows from its width or from `deps`. The same 1 px tolerance as
   * the box itself: sub-pixel text rounds one over with nothing cut (audit r3
   * FW-7 / D3-4).
   */
  cutSelector?: string,
): boolean => {
  const [overflowing, setOverflowing] = useState(false);

  const check = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setOverflowing(
      el.scrollWidth > el.clientWidth + 1 ||
        (cutSelector !== undefined &&
          Array.from(el.querySelectorAll<HTMLElement>(cutSelector)).some((c) => c.scrollWidth > c.clientWidth + 1)),
    );
  }, [ref, cutSelector]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    return observeOverflow(el, check);
  }, [ref, check]);

  useLayoutEffect(() => {
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check, ...deps]);

  return overflowing;
};
